import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { BehaviorSubject } from 'rxjs';
import { CaptchaMeta } from '../common/obj.lib';
import { captchaLength } from '../common/constants.lib';
import { LoggerService } from './logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class CaptchaServiceService {

  private _captchaMeta: BehaviorSubject<CaptchaMeta> = new BehaviorSubject<CaptchaMeta>(new CaptchaMeta());
  get captchaMeta$() {
    return this._captchaMeta.asObservable();
  }
  get captchaMeta() {
    return this._captchaMeta.getValue();
  }
  set captchaMeta(val: CaptchaMeta) {
    this._captchaMeta.next(val);
  }

  private _captchaImage: BehaviorSubject<string> = new BehaviorSubject<string>('');
  get captchaImage$() {
    return this._captchaImage.asObservable();
  }
  get captchaImage() {
    return this._captchaImage.getValue();
  }
  set captchaImage(val: string) {
    this.logger.debug(`setter:captchaImage: val: `, val);
    this._captchaImage.next(val);
  }

  private _captchaText: BehaviorSubject<string> = new BehaviorSubject<string>('');
  get captchaText$() {
    return this._captchaText.asObservable();
  }
  get captchaText() {
    return this._captchaText.getValue();
  }
  set captchaText(val: string) {
    this._captchaText.next(val);
  }

  private _captchaErrorMsg: BehaviorSubject<string> = new BehaviorSubject<string>('Captcha text is required')
  get captchaErrorMsg$() {
    return this._captchaErrorMsg.asObservable();
  }
  get captchaErrorMsg() {
    return this._captchaErrorMsg.getValue();
  }
  set captchaErrorMsg(val: string) {
    this._captchaErrorMsg.next(val);
  }

  private _iAmHumber: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  get iAmHuman$() {
    return this._iAmHumber.asObservable();
  }
  get iAmHuman() {
    return this._iAmHumber.getValue();
  }
  set iAmHuman(val: boolean | undefined) {
    this._iAmHumber.next(val);
  }

  get hasText() {
    return !!this.captchaText === true;
  }

  get textLength() {
    if (this.hasText) {
      return this.captchaText.length;
    }
    return 0;
  }

  private _hasCaptchaErrors: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get hasCaptchaErrors$() {
    return this._hasCaptchaErrors.asObservable();
  }
  get hasCaptchaErrors() {
    return this._hasCaptchaErrors.getValue();
  }
  set hasCaptchaErrors(val: boolean) {
    this._hasCaptchaErrors.next(val);
  }

  hasErrors() {
    this.hasCaptchaErrors = false;
    if (!this.captchaText) {
      // logger.debug(`hasCaptchaErrors:no captcha text...`)
      this.hasCaptchaErrors = true;
      return;
    }
    if (this.hasText && this.captchaText.length !== captchaLength) {
      // logger.debug(`hasCaptchaErrors: captcha text length not correct...`)
      this.hasCaptchaErrors = true;
      return;
    }
    if (!!this.iAmHuman === false) {
      // logger.debug(`hasCaptchaErrors: captcha iamhuman false...`)
      this.hasCaptchaErrors = true;
    }
  }

  private checkCaptchaText: boolean = false;

  constructor(
    private logger: LoggerService,
    private apiService: ApiService
  ) { }


  getCaptcha(): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`casicaptcha`, 'getCaptcha');
  }

  verifyCaptcha(captchaText: string): Promise<any> {
    return this.apiService.postHttpCallAsPromise('casicaptchaverify', { captcha: captchaText }, 'verifyCaptcha');
  }

  fetchCaptcha(): Promise<any> {
    return this.apiService.blobGet(`casicaptcha/`, 'fetchCaptcha');
  }

  verifyCaptchaInput(captchaText: string) {
    this.captchaText = captchaText;
    this.logger.debug(`verifyCaptchaInput: `, {
      captchaText, textLength: this.textLength,
      hasText: this.hasText
    });
    this.checkCaptchaText = false;
    if (this.hasText && this.textLength >= captchaLength) {
      this.checkCaptchaText = true;
      this.verifyCaptcha(captchaText)
        .then(resp => {
          this.logger.debug(`verifyCaptchaInput:resp: `, resp);
          this.iAmHuman = resp.success;
          this.captchaErrorMsg = resp.error;
        })
        .catch(err => {
          this.logger.error(`verifyCaptchaInput:error: `, err);
          this.iAmHuman = false;
          this.captchaErrorMsg = 'CAPTCHA error occurred';
        })
    }
  };

  verifyCaptchaInputTextOnly(captchaText: string, ignoreTextLength: boolean = false): Promise<CaptchaMeta> {
    // return new Promise((resolve, reject) => {
    this.logger.debug(`verifyCaptchaInputTextOnly:captchaText: `, captchaText);
    let _captchaMeta = new CaptchaMeta();
    _captchaMeta.captchaText = captchaText;
    _captchaMeta.checkCaptchaText = false;
    if (_captchaMeta.hasText && (ignoreTextLength || (!ignoreTextLength && _captchaMeta.textLength >= captchaLength))) {
      this.logger.debug(`actually verify captcha...`, _captchaMeta.captchaText);
      _captchaMeta.checkCaptchaText = true;
      return this.verifyCaptcha(_captchaMeta.captchaText)
        .then((resp: any) => {
          this.logger.debug(`verifyCaptchaInputTextOnly:resp: `, resp);
          _captchaMeta.iamhuman = resp.success;
          _captchaMeta.captchaErrorMsg = resp.error;
          return Promise.resolve(_captchaMeta);
          // $scope.login.iamhuman.$valid = vm.iamhuman;
        })
        .catch((err: any) => {
          this.logger.error(`verifyCaptchaInputTextOnly:error: `, err);
          _captchaMeta.iamhuman = false;
          _captchaMeta.captchaErrorMsg = 'CAPTCHA error occurred';
          return Promise.resolve(_captchaMeta);
        });
      // .finally(() => {
      //   this.logger.debug(`finally...`, _captchaMeta);
      //   // $rootScope.$broadcast('captchametadata', _captchaMeta);
      //   return Promise.resolve(_captchaMeta);
      // });
    } else {
      this.logger.debug(`doesn't have text...`);
      //  return Promise.resolve(_captchaMeta);
    }
    return Promise.resolve(_captchaMeta);
    // })
  };

  refreshCaptcha() {
    this.logger.debug(`refreshCaptcha: `, this.captchaMeta);
    this.resetFlags();
    this.fetchCaptchaImage();
  };

  fetchCaptchaImage() {
    this.logger.debug(`fetchCaptchaImage...`, this.captchaMeta);
    this.fetchCaptcha()
      .then(image => {
        this.logger.debug(`fetchCaptchaImage:fetched: `, image);
        setTimeout(() => {
          this.captchaImage = image;
        })
      })
      .catch(err => {
        this.logger.error(`fetchCaptchaImage:error: `, err.message);
        this.captchaImage = '';
      });
  }

  resetFlags() {
    this.captchaText = '';
    this.iAmHuman = undefined;
    this.checkCaptchaText = false;
    this.captchaErrorMsg = 'Captcha text is required';
  }

}
